@if (isLoading) {
  <div class="spinner-overlay">
    <mat-spinner></mat-spinner>
  </div>
}

<h1 class="title">@if (isReadonly) {
  Upscaling Rock Model
} @else {
  Upscaling Rock Model - Phases definition
}</h1>

@if (!isLoading && rockFile) {
<mat-card class="phase-definition-card">
  <div class="phase-definition-layout">
    <div class="left-panel">
      <div class="file-info-card">
        <div class="info-row">
          <span class="info-label">Rock File:</span>
          <span class="info-value">{{ fileName | removeExtension }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Size:</span>
          <span class="info-value">{{ getFileSizeDisplay() }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Phases count:</span>
          <span class="info-value">{{ phaseDefinitions.length }}</span>
        </div>
      </div>

      <div class="image-preview-card">
        <div class="image-header">Upscaling Segmented Rock Image</div>
        <div class="image-container">
          @if (thumbnailUrl) {
            <img [src]="thumbnailUrl" alt="Rock image thumbnail" class="rock-thumbnail" />
          } @else {
            <div class="placeholder-image">
              <mat-icon>image</mat-icon>
              <p>No preview available</p>
            </div>
          }
        </div>
      </div>
    </div>

    <div class="right-panel">
      <div class="rock-model-name-section">
        <div class="info-row">
          <span class="info-label">Rock Model Name</span>
          <form [formGroup]="rockModelNameForm" class="name-form">
            <mat-form-field appearance="outline" class="name-input">
              <input
                matInput
                formControlName="name"
                placeholder="e.g., Ups_01"
                maxlength="256"
                required
                [readonly]="isReadonly"
              />
              @if (!isReadonly && rockModelNameForm.get('name')?.hasError('required')) {
                <mat-error>Rock model name is required</mat-error>
              }
              @if (!isReadonly && rockModelNameForm.get('name')?.hasError('pattern')) {
                <mat-error>Only letters, numbers, hyphens and underscores allowed</mat-error>
              }
              @if (!isReadonly && rockModelNameForm.get('name')?.hasError('maxlength')) {
                <mat-error>Max 256 characters</mat-error>
              }
            </mat-form-field>
          </form>
        </div>
      </div>

      <div class="table-container-section">
        <h2 class="table-title">Labels properties</h2>
        <div class="table-wrapper">
          <table class="phase-table mat-elevation-z2">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Label</th>
                <th>Fraction</th>
                <th>Porosity<br/>Fraction</th>
                <th>Covered<br/>Porosity</th>
                <th>Permeability</th>
                <th>Formation<br/>Factor</th>
              </tr>
            </thead>
            <tbody>
              @for (phase of phaseDefinitions; track phase.label) {
                <tr>
                  <td class="color-cell">
                    <div class="phase-color-indicator" [style.background-color]="phase.color || 'rgb(128, 128, 128)'"></div>
                  </td>
                  <td class="phase-cell">{{ phase.label }}</td>
                  <td class="fraction-cell">{{ phase.fraction | number : '1.3-3' }}</td>
                  <td class="porosity-cell">
                    @if (!isReadonly) {
                      <input
                        type="number"
                        class="porosity-input"
                        [(ngModel)]="phase.porosity"
                        (blur)="onPorosityBlur(phase)"
                        (keydown.enter)="onPorosityBlur(phase)"
                        (keydown.escape)="onPorosityBlur(phase)"
                        min="0"
                        max="1"
                        step="0.01"
                        #porosityInput
                      />
                    } @else {
                      <span class="porosity-display readonly">
                        {{ phase.porosity != null ? (phase.porosity | number : '1.3-3') : '-' }}
                      </span>
                    }
                  </td>
                  <td
                    class="covered-porosity-cell"
                    [class.disabled]="isFieldDisabled(phase) || isReadonly"
                    [class.readonly]="isReadonly"
                  >
                    @if (isFieldDisabled(phase) || isReadonly) {
                      <span>{{ isFieldDisabled(phase) ? '-' : (phase.coveredPorosity != null ? (phase.coveredPorosity | number : '1.3-3') : (1 | number : '1.3-3')) }}</span>
                    } @else {
                      <input
                        type="number"
                        class="covered-porosity-input"
                        [(ngModel)]="phase.coveredPorosity"
                        (blur)="onCoveredPorosityBlur(phase)"
                        (keydown.enter)="onCoveredPorosityBlur(phase)"
                        min="0.01"
                        max="1"
                        step="0.01"
                      />
                    }
                  </td>
                  <td
                    class="selectable-cell"
                    [class.disabled]="isFieldDisabled(phase) || isReadonly"
                    [class.readonly]="isReadonly"
                    [class.selected]="!!phase.permeability"
                    (click)="selectPermeability(phase)"
                  >
                    @if (isFieldDisabled(phase)) {
                      <span>-</span>
                    } @else if (phase.permeability) {
                      <span class="selected-name">{{ phase.permeability.simulationName }}</span>
                    } @else {
                      <span class="select-placeholder">Select Kabs<br/>Cube trends</span>
                    }
                  </td>
                  <td
                    class="selectable-cell"
                    [class.disabled]="isFieldDisabled(phase) || isReadonly"
                    [class.readonly]="isReadonly"
                    [class.selected]="!!phase.formationFactor"
                    (click)="selectFormationFactor(phase)"
                  >
                    @if (isFieldDisabled(phase)) {
                      <span>-</span>
                    } @else if (phase.formationFactor) {
                      <span class="selected-name">{{ phase.formationFactor.simulationName }}</span>
                    } @else {
                      <span class="select-placeholder">Select FF<br/>Cube trends</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <div class="actions-row">
        @if (isReadonly) {
          <button mat-raised-button color="primary" (click)="back()">
            Back
          </button>
        } @else {
          <button mat-button (click)="cancel()">
            Cancel
          </button>
          <button
            mat-raised-button
            color="primary"
            [disabled]="!canSave()"
            (click)="save()"
          >
            Save
          </button>
        }
      </div>
    </div>
  </div>
</mat-card>
}
